/* /attendance — auto-populated from biometric devices, phones and manual punches.
 * Daily log (KPIs, filters, Source column) · Month grid per employee · Mobile check-in. */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { DayStatus, Employee } from "../../data/types";
import { useAllEmployees, useClock, useAttendanceDay, useAttendanceMonth, useBiometricOverview, useCheck, usePunches, useReportCsv, useSettings } from "../../data/hooks";
import { Alert, Button, Card, Chip, DualDate, EmptyState, Field, Icon, Input, Kpi, Loaded, PageHeader, Person, SearchBox, Seg, Select, Skeleton, errMsg, useToast } from "../../ui";
import { LOOKUP, useI18n } from "../../i18n";
import { useOverlays } from "../../app/overlays";
import { useSession } from "../../app/session";
import { downloadText, periodLabel } from "../../lib/format";
import { MonthGrid, monthStats } from "./components/MonthGrid";
import { DAY_STATUSES, dayStatusLabel, durText, hm, sourceLabel } from "./components/labels";
import "./time.css";

type View = "day" | "month";
const GEO = { lat: 21.5433, lng: 39.1728 };

export function StatusChip({ s, testId }: { s: DayStatus; testId?: string }) {
  const { L } = useI18n();
  return <Chip status={s} testId={testId}>{dayStatusLabel(s, L)}</Chip>;
}

function MobileCheckIn({ employeeId, today }: { employeeId: number; today: string }) {
  const { L, t } = useI18n();
  const toast = useToast();
  const check = useCheck();
  const mine = usePunches({ date: today, deviceId: "ess", employeeId, pageSize: 50 });
  const last = mine.data?.items[0];
  const checkedIn = last?.direction === "in";
  const go = () => check.mutate({ employeeId, kind: checkedIn ? "out" : "in", ...GEO }, {
    onSuccess: (p) => toast(p.direction === "in" ? L(`Checked in at ${hm(p.time)}`, `تم تسجيل الحضور ${hm(p.time)}`) : L(`Checked out at ${hm(p.time)}`, `تم تسجيل الانصراف ${hm(p.time)}`)),
    onError: (e) => { toast(errMsg(e), "bad"); void mine.refetch(); },
  });
  return (
    <Card title={L("Mobile check-in", "تسجيل الحضور بالجوال")} sub={L("Al Waha HQ · Al Rawdah, Jeddah · 150 m radius", "مقر الواحة · الروضة، جدة · نطاق 150 م")} testId="mobile-checkin">
      <div className="stack">
        <p className="sm muted" style={{ margin: 0 }}>{L("This is what employees see on their phone.", "هذا ما يراه الموظف على جواله.")}</p>
        <div className="geo"><span className="pulse" />{t("You're inside the office geofence")} · <span className="num" dir="ltr">21.5433° N, 39.1728° E</span></div>
        {mine.isLoading ? <Skeleton /> : (
          <button type="button" className={"checkin" + (checkedIn ? " out" : "")} onClick={go} disabled={check.isPending} data-testid="btn-checkin" aria-busy={check.isPending || undefined}>
            <Icon name={checkedIn ? "log-out" : "map-pin"} />{checkedIn ? t("Check out") : t("Check in")}
          </button>
        )}
        {last && <div className="sm muted" data-testid="checkin-last">{last.direction === "in" ? t("Checked in") : L("Checked out", "تم تسجيل الانصراف")} · <span className="num">{hm(last.time)}</span></div>}
      </div>
    </Card>
  );
}

export default function Attendance() {
  const i = useI18n();
  const { L, t } = i;
  const toast = useToast();
  const { openPrint } = useOverlays();
  const { user } = useSession();
  const { today } = useClock();
  const [view, setView] = useState<View>("day");
  /* null = follow the server clock (it may still be loading on first render). */
  const [dateSel, setDate] = useState<string | null>(null);
  const date = dateSel ?? today;
  const [q, setQ] = useState("");
  const [br, setBr] = useState("");
  const [st, setSt] = useState<"" | DayStatus>("");
  const [empSel, setEmpSel] = useState<number | null>(null);
  const [periodSel, setPeriod] = useState<string | null>(null);
  const period = periodSel ?? today.slice(0, 7);
  const day = useAttendanceDay(date || today);
  const emps = useAllEmployees();
  const settings = useSettings();
  const ov = useBiometricOverview();
  const csv = useReportCsv("attendance");
  const empId = empSel ?? user?.employeeId ?? day.data?.rows[0]?.employeeId ?? 0;
  const month = useAttendanceMonth(empId, period, { enabled: view === "month" && empId > 0 });
  const byId = useMemo(() => new Map((emps.data ?? []).map((e) => [e.id, e])), [emps.data]);
  const pol = settings.data?.attendance;

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (day.data?.rows ?? []).filter((r) => {
      const e = byId.get(r.employeeId);
      if (br && e?.branch !== br) return false;
      if (st && r.status !== st) return false;
      if (s && !(e && (e.nameEn.toLowerCase().includes(s) || e.nameAr.includes(q.trim()) || e.code.toLowerCase().includes(s)))) return false;
      return true;
    });
  }, [day.data, byId, q, br, st]);

  const exportCsv = async () => {
    const r = await csv.refetch();
    if (r.error) { toast(errMsg(r.error), "bad"); return; }
    downloadText(`attendance-${date || today}.csv`, r.data ?? "");
    toast(L("Attendance exported", "تم تصدير الحضور"));
  };

  const sum = day.data?.summary;
  const showCheck = user?.role === "hr" && user.employeeId != null;
  const canCheck = showCheck && (date || today) === today;
  const empOpts: [number, string][] = (emps.data ?? []).filter((e) => e.status !== "Terminated").map((e) => [e.id, i.name(e) + " · " + e.code]);
  const openMonth = (e: Employee) => { setEmpSel(e.id); setPeriod((date || today).slice(0, 7)); setView("month"); };

  const table = (
    <Card flush testId="daily-log">
      <div className="toolbar time-toolbar">
        <SearchBox value={q} onChange={setQ} placeholder={t("Search") + "…"} testId="att-search" />
        <Select value={br} onChange={(e) => setBr(e.target.value)} aria-label={t("Branch")} data-testid="att-branch" options={[["", t("All branches")], ...Object.keys(LOOKUP.BR).map((k): [string, string] => [k, i.branch(k)])]} />
        <Select value={st} onChange={(e) => setSt(e.target.value as DayStatus | "")} aria-label={t("Status")} data-testid="att-status" options={[["", L("All statuses", "كل الحالات")], ...DAY_STATUSES.map((s): [string, string] => [s, dayStatusLabel(s, L)])]} />
      </div>
      <Loaded q={day}>{() => rows.length === 0 ? <EmptyState title={L("No employees match", "لا يوجد موظفون مطابقون")} desc={L("Clear the search or filters.", "امسح البحث أو الفلاتر.")} /> : (
        <div className="tw"><table className="tbl cards compact" data-testid="att-table">
          <thead><tr><th>{t("Employee")}</th><th className="end">{t("Check-in")}</th><th className="end">{t("Check-out")}</th><th className="end hide-md">{L("Worked", "ساعات العمل")}</th><th className="end hide-md">{t("Overtime")}</th><th className="hide-md">{t("Source")}</th><th>{t("Status")}</th></tr></thead>
          <tbody>{rows.map((r) => {
            const e = byId.get(r.employeeId);
            return (
              <tr key={r.employeeId} className="click" onClick={() => e && openMonth(e)} data-testid={"att-row-" + r.employeeId}>
                <td className="k1">{e ? <button type="button" className="linkish" style={{ background: "none", border: 0, padding: 0, color: "inherit", font: "inherit", textAlign: "start", cursor: "pointer" }} onClick={(ev) => { ev.stopPropagation(); openMonth(e); }} aria-label={L("View month for ", "عرض شهر ") + i.name(e)}><Person e={e} meta={i.branch(e.branch)} /></button> : "#" + r.employeeId}</td>
                <td className="end k3" data-l={t("Check-in")}><span className="num" style={r.status === "Late" ? { color: "var(--warning)", fontWeight: 600 } : undefined}>{r.firstIn ?? "—"}</span>{r.lateMinutes > 0 && <span className="xs muted"> +{r.lateMinutes}{L("m", "د")}</span>}</td>
                <td className="end x" data-l={t("Check-out")}><span className="num">{r.lastOut ?? (r.firstIn && r.status !== "Missing punch" ? L("on shift", "في الدوام") : "—")}</span></td>
                <td className="end hide-md num sm">{durText(r.workedMinutes, L)}</td>
                <td className="end hide-md"><span className="num">{r.overtimeMinutes ? (r.overtimeMinutes / 60).toFixed(1) + L(" h", " س") : "—"}</span></td>
                <td className="hide-md sm muted" data-testid={"src-" + r.employeeId}>{sourceLabel(r.source, L)}</td>
                <td className="k2"><StatusChip s={r.status} testId={"st-" + r.employeeId} /></td>
              </tr>
            );
          })}</tbody>
        </table></div>
      )}</Loaded>
    </Card>
  );

  const monthEmp = byId.get(empId);
  const monthView = (
    <Card testId="month-view" title={monthEmp ? `${periodLabel(period, i.lang)} · ${i.name(monthEmp)}` : periodLabel(period, i.lang)} sub={L("Pick an employee from the daily log to see their month", "اختر موظفًا من السجل اليومي لعرض شهره")}>
      <div className="time-toolbar mb">
        <Field label={t("Employee")} htmlFor="m-emp"><Select id="m-emp" value={empId} onChange={(e) => setEmpSel(Number(e.target.value))} options={empOpts} data-testid="m-emp" /></Field>
        <Field label={L("Month", "الشهر")} htmlFor="m-period"><Input id="m-period" type="month" value={period} onChange={(e) => e.target.value && setPeriod(e.target.value)} data-testid="m-period" /></Field>
      </div>
      <Loaded q={month}>{(mrows) => {
        const s = monthStats(mrows);
        return (
          <>
            <div className="stat-line mb" data-testid="month-stats">
              <span>{L("Present", "حاضر")} <b>{s.present}</b></span><span>{L("Late", "متأخر")} <b>{s.late}</b></span><span>{L("Absent", "غائب")} <b>{s.absent}</b></span>
              <span>{L("Missing punch", "بصمة ناقصة")} <b>{s.missing}</b></span><span>{L("On leave", "في إجازة")} <b>{s.leave}</b></span><span>{L("Overtime", "الإضافي")} <b>{s.otHours}</b> {L("h", "س")}</span>
            </div>
            <MonthGrid period={period} rows={mrows} today={today} />
          </>
        );
      }}</Loaded>
    </Card>
  );

  return (
    <div data-testid="attendance-page">
      <PageHeader title={t("Attendance")}
        sub={<><DualDate date={date || today} inline /> · {pol ? L(`Shift ${pol.shiftStart}–${pol.shiftEnd} · ${pol.graceMinutes} min grace`, `الوردية ${pol.shiftStart}–${pol.shiftEnd} · سماح ${pol.graceMinutes} دقيقة`) : ""}</>}
        actions={<>
          <Seg<View> value={view} onChange={setView} label={L("View", "العرض")} items={[["day", t("Daily log")], ["month", L("Month", "الشهر")]]} />
          <Input type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} aria-label={L("Date", "التاريخ")} data-testid="att-date" style={{ width: "auto" }} />
          <Button icon="printer" onClick={() => openPrint({ doc: "attendance", date: date || today })} data-testid="btn-print">{t("Print")}</Button>
          <Button icon="download" onClick={exportCsv} loading={csv.isFetching} data-testid="btn-export">{t("Export")}</Button>
        </>} />

      {ov.data && ov.data.offline > 0 && (
        <div className="mb"><Alert tone="warn" testId="banner-offline"
          title={L(`${ov.data.offline} device${ov.data.offline > 1 ? "s are" : " is"} offline`, `${ov.data.offline} ${ov.data.offline > 1 ? "أجهزة غير متصلة" : "جهاز غير متصل"}`)}
          desc={L("Punches are kept on the device and appear here as soon as it reconnects.", "تُحفظ البصمات في الجهاز وتظهر هنا فور عودة الاتصال.")}
          action={<Link className="btn sm" to="/biometric" data-testid="banner-offline-link">{L("Check devices", "فحص الأجهزة")}</Link>} /></div>
      )}
      {ov.data && ov.data.unmatched > 0 && (
        <div className="mb"><Alert tone="info" testId="banner-unmatched"
          title={L(`${ov.data.unmatched} device user${ov.data.unmatched > 1 ? "s aren't" : " isn't"} linked to an employee`, `${ov.data.unmatched} من مستخدمي الأجهزة غير مرتبطين بموظفين`)}
          desc={L("Their punches don't count yet. Link them once and past days are fixed automatically.", "بصماتهم لا تُحتسب بعد. اربطهم مرة واحدة وستُصحَّح الأيام السابقة تلقائيًا.")}
          action={<Link className="btn sm" to="/biometric/unmatched" data-testid="banner-unmatched-link">{L("Link users", "ربط المستخدمين")}</Link>} /></div>
      )}

      <div className="kpi5 mb" data-testid="att-kpis">
        <Kpi label={t("Present")} value={sum ? String(sum.Present) : "—"} icon="check" foot={day.data ? L("of " + day.data.rows.length, "من " + day.data.rows.length) : undefined} testId="kpi-present" />
        <Kpi label={t("Late")} value={sum ? String(sum.Late) : "—"} icon="clock" tone="warn" testId="kpi-late" />
        <Kpi label={t("Absent")} value={sum ? String(sum.Absent) : "—"} icon="x" tone="bad" foot={sum && sum["Not in yet"] ? L(`+${sum["Not in yet"]} not in yet`, `+${sum["Not in yet"]} لم يحضروا بعد`) : undefined} testId="kpi-absent" />
        <Kpi label={L("Missing punch", "بصمة ناقصة")} value={sum ? String(sum["Missing punch"]) : "—"} icon="alert" tone="warn" foot={L("No check-out recorded", "لم يُسجَّل انصراف")} testId="kpi-missing" />
        <Kpi label={L("Overtime", "العمل الإضافي")} value={sum ? sum.overtimeHours.toFixed(1) : "—"} unit={L("hrs", "ساعة")} icon="zap" tone="info" testId="kpi-ot" />
      </div>

      {view === "day" ? (showCheck ? (
        <div className="g g-2-1">{table}<div className="stack">
          {canCheck ? <MobileCheckIn employeeId={user.employeeId as number} today={today} /> : <Alert tone="info" tint title={L("Mobile check-in works for today only", "تسجيل الحضور بالجوال متاح لليوم فقط")} />}
        </div></div>
      ) : table) : monthView}
    </div>
  );
}
